import { useRouter } from 'next/router';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import { GetServerSideProps } from 'next';
import { basicPageProps, fetchTemplate, mobileProps } from '@/utils/isServer';
import Head from 'next/head';
import dayjs from 'dayjs';
import { Breadcrumb, Collapse } from 'antd';
import { cacheFetch } from '@/helpers/cacheFetch';
import { CDN1, CDN2 } from '@/helpers/api';
import { useEffect, useRef } from 'react';
import SearchStore from '@/stores/SearchStore';
import { useMobxStores } from '@/stores';

const ContentHeader = dynamic(() => import('@components/ContentHeader'), {
  loading: () => <p>Loading</p>,
});

const SectionItem = dynamic(() => import('@components2/Landing/SectionItem'), {
  ssr: true,
});

const { Panel } = Collapse;

function extractPrefixSuffix(url) {
  let supressFromHome = false;
  let suffix = '';
  let prefix = '/pressroom/';
  if (url.indexOf('?') != -1) {
    suffix = url.substring(url.indexOf('?'));
  }
  if (url.indexOf('/myapp') != -1) {
    prefix = '/myapp/pressroom/';
  }
  if (suffix == '?fromhome=true') {
    suffix = '';
    supressFromHome = true;
  }
  return [prefix, suffix, supressFromHome];
}

export const getServerSideProps: GetServerSideProps = async ({
  req,
  params,
  query,
}) => {
  const { isMobile } = mobileProps({ req });
  const { configInfo, airports, baseUrl } = await basicPageProps({ req });
  const { t } = query; // Preview
  let landing;
  const friendlyUrl = params.id as string;
  try {
    if (t === 'preview') {
      const landingResponse = await fetch(
        `${baseUrl}/api/AirportLandingPress/${friendlyUrl}?t=preview`,
        { ...fetchTemplate }
      );
      landing = await landingResponse.json();
    } else {
      landing = await cacheFetch(
        `${baseUrl}/api/AirportLandingPress/${friendlyUrl}`
      );
    }
    if (
      t !== 'preview' &&
      landing.press_friendly_url &&
      landing.press_friendly_url != friendlyUrl
    ) {
      const [prefix, suffix] = extractPrefixSuffix(req.url);
      return {
        redirect: {
          destination: prefix + landing.press_friendly_url + suffix,
          permanent: true,
        },
      } as any;
    }
  } catch (e) {
    return {
      redirect: {
        destination: '/',
      },
    } as any;
  }
  const [prefix, suffix, supressFromHome] = extractPrefixSuffix(req.url);
  if (supressFromHome) {
    return {
      redirect: {
        destination: prefix + landing.airport_frienldy_url + suffix,
        permanent: true,
      },
    } as any;
  }

  return {
    props: {
      airports,
      landing,
      configInfo,
      deviceType: isMobile ? 'mobile' : 'desktop',
    },
  };
};

export default function NewsDetail({
  airports,
  landing,
  configInfo,
  deviceType,
}) {
  const router = useRouter();
  const landingAirportInitials = landing?.properties?.airport_initials;
  const pageTitle = landing?.properties?.seo_title
    ? landing?.properties?.seo_title
    : `${landing?.properties?.airport_name ?? ''} (${
        landingAirportInitials ?? ''
      }) | On Air Parking Cheap Discount Rates`;
  const metaTitle = pageTitle;
  const metaStaticBlurb = `Cheap ${
    landing?.properties?.airport_name ?? ''
  } Parking At ${
    landingAirportInitials ?? ''
  }. We know, the price looks to good to be true! Don't worry, it's true, and it's a great location. In fact you'll be parking at a professional parking facility. You'll be e-mailed where you're parking for $2.75 a day once you make the purchase on our site. Reserve today!`;
  const metaDescription = landing?.properties?.landing_seo_description
    ? landing?.properties?.landing_seo_description
    : metaStaticBlurb;
  const metaKeywords = landing?.landing_seo_keywords ?? '';

  const {
    searchStore,
  }: {
    searchStore: SearchStore;
  } = useMobxStores();

  useEffect(() => {
    const query = { ...router.query };
    query.facility_id = landing?.extra?.event_facility_id;
    query.checkin = landing?.extra?.event_checkin;
    query.checkout = landing?.extra?.event_checkout;
    searchStore.fetchSearchResult(null, query);
  }, [router, router?.query, searchStore]);

  return (
    <>
      <Head>
        <title>{pageTitle} | On Air Parking</title>
        <meta property="og:title" content={metaTitle} key="og:title" />
        <meta
          property="og:description"
          content={metaDescription}
          key="og:description"
        />
        <meta name="description" content={metaDescription} key="description" />
        {metaKeywords && (
          <meta name="keywords" content={metaKeywords} key="keywords" />
        )}
        {landing?.metainfo?.map((meta) => meta.content)}
        {landing?.properties?.landing_display_header}
        {landing?.schemas?.map((schema, index) => (
          <script
            type="application/ld+json"
            key={index}
            dangerouslySetInnerHTML={{
              __html: schema.schema_object,
            }}
          ></script>
        ))}
      </Head>
      <div className="max-w-4xl mx-auto my-12 px-4">
        <article>
          <div className="high-content flex flex-col gap-2">
            {landing?.sections.map((section, index) =>
              section.type !== 'Accordion' ? (
                <>
                  <ContentHeader btitle={section?.tag} />
                  <section key={index}>
                    <Breadcrumb className="text-sm md:text-base mt-4 mb-8">
                      <Breadcrumb.Item>
                        <a href="/parking/pressroom">Pressroom</a>
                      </Breadcrumb.Item>
                      <Breadcrumb.Item>{section?.tag}</Breadcrumb.Item>
                    </Breadcrumb>

                    <Image
                      src={`${CDN1}/upload/${
                        configInfo.isSandBoxEnvironment
                          ? 1
                          : configInfo.isProductionEnvironment
                          ? 2
                          : 0
                      }/${landing?.extra.event_image}`}
                      width={600}
                      height={400}
                      className="place-self-center"
                      objectFit="cover"
                      alt="Event Background"
                    />
                    <p className="text-right mt-6 mb-4 text-sm text-gray-500">
                      {dayjs(landing.dt_last_deploy).format('MMMM D, YYYY')}
                    </p>
                    <SectionItem
                      section={section}
                      configInfo={configInfo}
                      fallbackImageAlt={landing?.page_title}
                      isPressRoom
                    />
                  </section>
                </>
              ) : (
                <Collapse collapsible="header" key={index}>
                  <Panel header={section?.tag} key={index}>
                    <SectionItem
                      section={section}
                      configInfo={configInfo}
                      fallbackImageAlt={landing?.page_title}
                    />
                  </Panel>
                </Collapse>
              )
            )}
          </div>
          <button
            onClick={() => router.back()}
            className="mt-6 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
          >
            Back
          </button>
        </article>
      </div>
    </>
  );
}
